import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapPin, Navigation, Search, Crosshair, PackagePlus, Loader2, Route as RouteIcon } from "lucide-react";
import api, { formatRON } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLACEHOLDER = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=600";

function DistanceBadge({ km }) {
  if (km == null) return null;
  const label = km === 0 ? "În județul tău" : `≈ ${km} km`;
  const tone = km === 0 ? "bg-emerald-600" : km <= 100 ? "bg-emerald-600" : km <= 250 ? "bg-amber-500 text-slate-900" : "bg-slate-700";
  return (
    <span className={`inline-flex items-center gap-1 ${tone} text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1`} data-testid="request-distance">
      <RouteIcon className="w-3 h-3" /> {label}
    </span>
  );
}

function RequestCard({ r }) {
  return (
    <div className="group border border-slate-200 bg-white flex flex-col hover:border-orange-400 transition-colors" data-testid={`request-card-${r.id}`}>
      <Link to={`/anunt/${r.id}`} className="block relative overflow-hidden bg-slate-100 aspect-[16/9]">
        <img src={r.image || PLACEHOLDER} alt={r.name} loading="lazy" decoding="async"
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute top-0 left-0 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5" data-testid="request-badge">Cerere</span>
        <span className="absolute bottom-2 right-2"><DistanceBadge km={r.distance_km} /></span>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        {r.mk_subcategory_name && <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-600 mb-1">{r.mk_subcategory_name}</span>}
        <Link to={`/anunt/${r.id}`}>
          <h3 className="font-bold text-slate-900 leading-snug mb-3 line-clamp-2 hover:text-orange-600 transition-colors">{r.name}</h3>
        </Link>
        {(r.location || r.county) && (
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-3"><MapPin className="w-3.5 h-3.5" /> {[r.location, r.county].filter(Boolean).join(", ")}</div>
        )}
        <div className="mt-auto flex items-end justify-between gap-2 pt-3 border-t border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Buget estimat</div>
            <div className="text-lg font-black text-slate-900 tracking-tight">{r.price_on_request || !r.price ? "Negociabil" : formatRON(r.price)}</div>
          </div>
          <Link to={`/anunt/${r.id}`} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`request-respond-${r.id}`}>
            Răspunde
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function RequestsWall() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [refCounty, setRefCounty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState([]);
  const [counties, setCounties] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [coords, setCoords] = useState(null);
  const [filters, setFilters] = useState({ mk_primary: "", county: "", search: "", from_county: "" });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    api.get("/marketplace-taxonomy").then((r) => setTree(r.data.tree)).catch(() => {});
    api.get("/ro-locations").then((r) => setCounties(r.data.counties)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setFilters((f) => ({ ...f, search: searchInput.trim() })), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const fetchRequests = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.mk_primary) params.mk_primary = filters.mk_primary;
    if (filters.county) params.county = filters.county;
    if (filters.search) params.search = filters.search;
    if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
    else if (filters.from_county) params.from_county = filters.from_county;
    api.get("/requests", { params })
      .then((r) => { setRequests(r.data.requests || []); setRefCounty(r.data.reference_county); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters, coords]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizarea nu este disponibilă în browser."); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setFilters((f) => ({ ...f, from_county: "" }));
        setGeoLoading(false);
        toast.success("Locația ta a fost detectată — distanțele sunt calculate.");
      },
      () => { setGeoLoading(false); toast.error("Nu am putut accesa locația. Alege manual un județ."); },
      { timeout: 8000 }
    );
  };

  const setReferenceCounty = (v) => {
    setCoords(null);
    setFilters((f) => ({ ...f, from_county: v === "__none" ? "" : v }));
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="requests-wall-page">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-orange-600 mb-2">
            <Navigation className="w-3.5 h-3.5" /> Panou cereri · Logistică Geo
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Ce se caută acum</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">Cereri de utilaje, materiale și servicii de la cumpărători verificați. Setează un punct de referință și vezi instant distanța logistică pentru fiecare cerere.</p>
        </div>
        <Link to={user ? "/vinde" : "/autentificare"} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 font-bold uppercase tracking-wide text-sm transition-colors shrink-0" data-testid="post-request-cta">
          <PackagePlus className="w-4 h-4" /> Publică o cerere
        </Link>
      </div>

      {/* Geo reference bar */}
      <div className="border border-slate-200 bg-slate-50 p-4 mb-8 flex flex-col md:flex-row md:items-end gap-4" data-testid="geo-reference-bar">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide font-bold text-slate-500 flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5" /> Punct de referință</label>
          <Select value={coords ? "__geo" : (filters.from_county || "__none")} onValueChange={setReferenceCounty} disabled={!!coords}>
            <SelectTrigger className="rounded-none mt-1.5 bg-white" data-testid="ref-county-select"><SelectValue placeholder="Alege un județ" /></SelectTrigger>
            <SelectContent className="max-h-[50vh]">
              <SelectItem value="__none">Fără (nesortat)</SelectItem>
              {coords && <SelectItem value="__geo">Locația mea (GPS)</SelectItem>}
              {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button onClick={useMyLocation} disabled={geoLoading}
          className="inline-flex items-center justify-center gap-2 border border-slate-900 hover:bg-slate-900 hover:text-white px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
          data-testid="use-my-location-btn">
          {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
          Folosește locația mea
        </button>
        {refCounty && (
          <div className="text-xs text-slate-500 md:pb-3" data-testid="ref-county-label">
            Distanțe față de <span className="font-bold text-slate-800">{refCounty}</span>{coords ? " (aproximat din GPS)" : ""}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Caută în cereri (titlu, descriere, locație)..."
            className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm focus:outline-none focus:border-orange-500"
            data-testid="requests-search" />
        </div>
        <Select value={filters.mk_primary || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, mk_primary: v === "__all" ? "" : v }))}>
          <SelectTrigger className="rounded-none sm:w-64" data-testid="requests-category"><SelectValue placeholder="Toate categoriile" /></SelectTrigger>
          <SelectContent className="max-h-[50vh]">
            <SelectItem value="__all">Toate categoriile</SelectItem>
            {tree.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.county || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, county: v === "__all" ? "" : v }))}>
          <SelectTrigger className="rounded-none sm:w-48" data-testid="requests-county"><SelectValue placeholder="Toate județele" /></SelectTrigger>
          <SelectContent className="max-h-[50vh]">
            <SelectItem value="__all">Toate județele</SelectItem>
            {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="text-sm text-slate-500 mb-4" data-testid="requests-count">{loading ? "Se încarcă..." : `${requests.length} cereri active`}</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <div key={i} className="border border-slate-200 aspect-[4/3] animate-pulse bg-slate-100" />)
        ) : requests.length === 0 ? (
          <div className="col-span-full border border-slate-200 p-12 text-center" data-testid="no-requests">
            <p className="text-slate-500">Nicio cerere pentru filtrele selectate.</p>
            <Link to={user ? "/vinde" : "/autentificare"} className="inline-block mt-4 text-orange-600 font-semibold hover:underline">Publică prima cerere</Link>
          </div>
        ) : (
          requests.map((r) => <RequestCard key={r.id} r={r} />)
        )}
      </div>
    </div>
  );
}
